/**
 * SelectableChipView — presentational half of the shared selected/unselected
 * chip (SE-5527, DPL standardization).
 *
 * Atoms only, semantic tokens only. The selected/unselected class pairings
 * live in a single closed map (same pairing style as the Badge atom's
 * `TONE_CLASS`) so feature code never hand-rolls chip chrome again: selected
 * is the accent fill + on-accent text; unselected is a raised surface with a
 * default outline + secondary text. NativeWind resolves light/dark from the
 * tokens, so there is no `isDark` prop.
 *
 * Shapes: `pill` (rounded-full, caption text — ex-FormationPill) and `chip`
 * (rounded-full, body text — ex-FormationChip / MobileRoleChip). Chips are
 * fully rounded per the role radii (SE-8552: chips full · buttons sm · cards
 * md · modals lg · sheets xl); `chip` differs from `pill` only in padding and
 * text size. The fixed-size `score` tiles keep rounded-md.
 * @module components/molecules/SelectableChip/SelectableChipView
 */
import type { LucideIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import type { TextVariant } from "@/components/atoms/types";

/** Selection state → box/text class pairing (closed — never spelled inline). */
const STATE_CLASS: Record<
  "selected" | "unselected",
  { readonly box: string; readonly text: string }
> = {
  selected: {
    box: "border-accent-primary bg-accent-primary",
    text: "text-content-on-accent",
  },
  unselected: {
    // White with the subtle outline, per the ratified chip: #fff resolves to
    // `surface-base` and #e6e6e6 to `outline-subtle`, both exactly. It was
    // `surface-raised` (#f6f6f6) on `outline-default` (#dddcdb) — a grey chip
    // on a white page, which reads as disabled rather than as unselected.
    box: "border-outline-subtle bg-surface-base",
    text: "text-content-secondary",
  },
};

/** Shape → radius/padding + text variant pairing (closed). */
const SHAPE_CLASS: Record<
  "pill" | "chip" | "score" | "score-compact",
  { readonly box: string; readonly textVariant: TextVariant }
> = {
  pill: { box: "rounded-full px-3 py-1", textVariant: "caption" },
  chip: { box: "rounded-full px-3 py-2", textVariant: "body" },
  // `score` is FIXED-SIZE and carries no horizontal padding, and both halves
  // of that are load-bearing (SE-7281).
  //
  // A padded, content-sized chip is fine on its own and fatal in a row of
  // eleven: the score scale used to hand each chip `flex-1`, which on Yoga
  // means `flexBasis: 0`, so all eleven split the width and the chip's own
  // `px-3` then consumed what was left. The digits rendered to zero width —
  // eleven blank boxes on a real device, while web's `min-width: auto` let
  // content push back and looked perfectly correct.
  //
  // 48x44 is the design's own measurement (frame 32, `Btn/0`…`Btn/10`), which
  // also specifies `shrink-0`: the row wraps rather than compressing.
  score: { box: "h-11 w-12 rounded-md", textVariant: "body" },
  // The WEB inline editor draws the same control smaller, and both numbers
  // are the design's own: frame 32's `Btn/0` measures 48x44 (mobile
  // step-through), while frames 12/29's `RatingPill` measures 32x32 (the
  // inline editor, `70:1378`…). One shape cannot serve both — shrinking
  // `score` to 32 would re-open SE-7281, where eleven chips collapsed to
  // zero width on a real device while web looked perfectly correct.
  //
  // Still fixed-size and still unpadded, for the same SE-7281 reason.
  "score-compact": { box: "size-8 rounded-md", textVariant: "body" },
};

/**
 * Resolves the selection-state class pairing.
 * @param isSelected - Whether the chip is selected
 * @returns The box/text class pairing for the state.
 */
const stateOf = (isSelected: boolean) =>
  STATE_CLASS[isSelected ? "selected" : "unselected"];

/** Props for SelectableChipView. */
export interface SelectableChipViewProps {
  /** Chip label. */
  readonly label: string;
  /** Whether the chip is currently selected. */
  readonly isSelected: boolean;
  /** Press handler (already bound to its value by the feature Container). */
  readonly onPress: () => void;
  /**
   * Chip silhouette.
   *
   * `pill` = rounded-full, `chip` = rounded-full and content-sized, `score` =
   * a fixed 48x44 tile that never shrinks — for rows dense enough that a
   * content-sized chip would be squeezed until its label disappears.
   */
  readonly shape?: "pill" | "chip" | "score" | "score-compact";
  /** Optional leading lucide icon. */
  readonly icon?: LucideIcon;
  /** Test identity forwarded to the pressable. */
  readonly testID?: string;
  /** Disables the press affordance and dims the chip. */
  readonly disabled?: boolean;
  /** A11y label override — defaults to the label. */
  readonly accessibilityLabel?: string;
}

/**
 * Renders the shared selectable chip.
 * @param root0 - Component props
 * @param root0.label - Chip label
 * @param root0.isSelected - Whether the chip is selected
 * @param root0.onPress - Press handler
 * @param root0.shape - Chip silhouette (defaults to `chip`)
 * @param root0.icon - Optional leading lucide icon
 * @param root0.testID - Test identity
 * @param root0.disabled - Disables the press affordance
 * @param root0.accessibilityLabel - A11y label override
 * @returns The chip element.
 */
const SelectableChipView = ({
  label,
  isSelected,
  onPress,
  shape = "chip",
  icon,
  testID,
  disabled = false,
  accessibilityLabel,
}: SelectableChipViewProps) => (
  <Pressable
    testID={testID}
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel ?? label}
    accessibilityState={{ selected: isSelected, disabled }}
    className={`flex-row items-center justify-center gap-1 border ${SHAPE_CLASS[shape].box} ${stateOf(isSelected).box} ${disabled ? "opacity-50" : ""}`}
  >
    {icon ? (
      <Icon as={icon} size="2xs" className={stateOf(isSelected).text} />
    ) : null}
    <Text
      variant={SHAPE_CLASS[shape].textVariant}
      className={`text-center ${stateOf(isSelected).text}`}
    >
      {label}
    </Text>
  </Pressable>
);

SelectableChipView.displayName = "SelectableChipView";

export default memo(SelectableChipView);
